/**
 * Small download helpers for ID proof images.
 * No external dependencies — the ZIP writer below produces a plain
 * "stored" (uncompressed) archive, which is ideal for images that are
 * already compressed (JPEG/PNG).
 */

function sanitizeFileNamePart(name, fallback = 'file') {
  if (name === null || name === undefined) return fallback;
  let cleaned = String(name).trim().replace(/\s+/g, '_').replace(/[^a-zA-Z0-9._-]+/g, '_').replace(/_+/g, '_').replace(/^[_.]+|[_.]+$/g, '');
  if (!cleaned) cleaned = fallback;
  // Keep names filesystem-friendly
  if (cleaned.length > 120) cleaned = cleaned.slice(0, 120);
  return cleaned;
}

function triggerBlobDownload(blob, filename) {
  const url = URL.createObjectURL(blob);
  const link = document.createElement('a');
  link.href = url;
  link.download = filename;
  document.body.appendChild(link);
  link.click();
  link.remove();
  // Revoke after a tick so the download has time to start
  setTimeout(() => URL.revokeObjectURL(url), 5000);
}

async function fetchUrlAsBytes(url) {
  const response = await fetch(url, { mode: 'cors' });
  if (!response.ok) {
    throw new Error(`Failed to fetch file (HTTP ${response.status})`);
  }
  return new Uint8Array(await response.arrayBuffer());
}

async function downloadUrlAsFile(url, filename) {
  const response = await fetch(url, { mode: 'cors' });
  if (!response.ok) {
    throw new Error(`Failed to fetch file (HTTP ${response.status})`);
  }
  const blob = await response.blob();
  triggerBlobDownload(blob, filename);
}

// ---- Minimal stored-ZIP writer (no compression) ----

const CRC_TABLE = (() => {
  const table = new Uint32Array(256);
  for (let n = 0; n < 256; n++) {
    let c = n;
    for (let k = 0; k < 8; k++) {
      c = c & 1 ? 0xedb88320 ^ (c >>> 1) : c >>> 1;
    }
    table[n] = c >>> 0;
  }
  return table;
})();

function crc32(bytes) {
  let crc = 0xffffffff;
  for (let i = 0; i < bytes.length; i++) {
    crc = CRC_TABLE[(crc ^ bytes[i]) & 0xff] ^ (crc >>> 8);
  }
  return (crc ^ 0xffffffff) >>> 0;
}

function writeUint16(view, offset, value) {
  view.setUint16(offset, value, true);
}

function writeUint32(view, offset, value) {
  view.setUint32(offset, value, true);
}

/**
 * Build a ZIP archive (stored entries, UTF-8 names) and return it as a Blob.
 * @param {Array<{ name: string, data: Uint8Array }>} entries
 */
function createStoredZip(entries) {
  const encoder = new TextEncoder();
  const encoded = entries.map((entry) => ({
    nameBytes: encoder.encode(entry.name),
    data: entry.data,
    crc: crc32(entry.data),
  }));

  let totalSize = 0;
  for (const entry of encoded) {
    totalSize += 30 + entry.nameBytes.length + entry.data.length; // local header + name + data
  }
  const centralStart = totalSize;
  for (const entry of encoded) {
    totalSize += 46 + entry.nameBytes.length; // central header + name
  }
  totalSize += 22; // end of central directory

  const buffer = new ArrayBuffer(totalSize);
  const bytes = new Uint8Array(buffer);
  const view = new DataView(buffer);
  let offset = 0;
  const centralOffsets = [];

  for (const entry of encoded) {
    centralOffsets.push(offset);
    // Local file header
    writeUint32(view, offset, 0x04034b50); offset += 4; // signature
    writeUint16(view, offset, 20); offset += 2; // version needed
    writeUint16(view, offset, 0x0800); offset += 2; // UTF-8 filename flag
    writeUint16(view, offset, 0); offset += 2; // method: stored
    writeUint16(view, offset, 0); offset += 2; // mod time
    writeUint16(view, offset, 0); offset += 2; // mod date
    writeUint32(view, offset, entry.crc); offset += 4;
    writeUint32(view, offset, entry.data.length); offset += 4; // compressed size
    writeUint32(view, offset, entry.data.length); offset += 4; // uncompressed size
    writeUint16(view, offset, entry.nameBytes.length); offset += 2;
    writeUint16(view, offset, 0); offset += 2; // extra length
    bytes.set(entry.nameBytes, offset); offset += entry.nameBytes.length;
    bytes.set(entry.data, offset); offset += entry.data.length;
  }

  for (let i = 0; i < encoded.length; i++) {
    const entry = encoded[i];
    writeUint32(view, offset, 0x02014b50); offset += 4; // central signature
    writeUint16(view, offset, 20); offset += 2; // version made by
    writeUint16(view, offset, 20); offset += 2; // version needed
    writeUint16(view, offset, 0x0800); offset += 2; // UTF-8 flag
    writeUint16(view, offset, 0); offset += 2; // method: stored
    writeUint16(view, offset, 0); offset += 2; // mod time
    writeUint16(view, offset, 0); offset += 2; // mod date
    writeUint32(view, offset, entry.crc); offset += 4;
    writeUint32(view, offset, entry.data.length); offset += 4;
    writeUint32(view, offset, entry.data.length); offset += 4;
    writeUint16(view, offset, entry.nameBytes.length); offset += 2;
    writeUint16(view, offset, 0); offset += 2; // extra length
    writeUint16(view, offset, 0); offset += 2; // comment length
    writeUint16(view, offset, 0); offset += 2; // disk number
    writeUint16(view, offset, 0); offset += 2; // internal attrs
    writeUint32(view, offset, 0); offset += 4; // external attrs
    writeUint32(view, offset, centralOffsets[i]); offset += 4; // local header offset
    bytes.set(entry.nameBytes, offset); offset += entry.nameBytes.length;
  }

  const centralSize = offset - centralStart;

  // End of central directory
  writeUint32(view, offset, 0x06054b50); offset += 4;
  writeUint16(view, offset, 0); offset += 2; // disk number
  writeUint16(view, offset, 0); offset += 2; // central dir disk
  writeUint16(view, offset, encoded.length); offset += 2; // entries on disk
  writeUint16(view, offset, encoded.length); offset += 2; // total entries
  writeUint32(view, offset, centralSize); offset += 4;
  writeUint32(view, offset, centralStart); offset += 4;
  writeUint16(view, offset, 0); offset += 2; // comment length

  return new Blob([buffer], { type: 'application/zip' });
}

export {
  sanitizeFileNamePart,
  triggerBlobDownload,
  fetchUrlAsBytes,
  downloadUrlAsFile,
  createStoredZip,
};
